import { ExerciseStateMachine } from './ExerciseStateMachine';
import { calculateJointAngle } from './FsmUtils';

export const OverheadReachState = {
  DOWN: 'DOWN',          
  RAISING: 'RAISING',    
  UP: 'UP',              
  LOWERING: 'LOWERING'   
};

const MOVEMENT_THRESHOLD = 0.02;
const MIN_TIME_IN_POSITION = 100; 

export class OverheadReachFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: OverheadReachState.DOWN,
      initialStateData: {
        previousWristHeight: 0,
        isMovingUp: false,
        isMovingDown: false,
        lastPositions: [],
        rangeOfMotion: 0,
        maxRangeOfMotion: 0, // Track maximum ROM achieved
        movementSmoothness: 0,
        lastBelowShoulderTime: 0,
        lastAboveHeadTime: 0,
        exerciseProgress: 0,
        repFormScore: 0
      },
      initialFormScores: {
        overall: 100,
        rangeOfMotion: 0,
        velocity: 0,
        control: 100,
        balance: 100,
        symmetry: 100
      },
      transitions: {
        [OverheadReachState.DOWN]: [
          {
            targetState: OverheadReachState.RAISING,
            condition: (metrics, stateData, timestamp) => 
              metrics.isMovingUp && !metrics.isBelowShoulder,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastBelowShoulderTime = timestamp;
            }
          }
        ],
        
        [OverheadReachState.RAISING]: [
          {
            targetState: OverheadReachState.UP,
            condition: (metrics, stateData, timestamp) => 
              metrics.isAboveHead && timestamp - stateData.lastBelowShoulderTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastAboveHeadTime = timestamp;
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              // Use MAX ROM achieved during rep, not current ROM at completion moment
              const maxROM = stateData.maxRangeOfMotion || metrics.rangeOfMotion || 0;
              return Math.round(
                (maxROM * 0.5) + 
                (metrics.velocityScore * 0.3) + 
                (metrics.movementSmoothness * 0.2)
              );
            }
          },
          {
            targetState: OverheadReachState.DOWN,
            condition: (metrics, stateData, timestamp) => 
              metrics.isBelowShoulder && metrics.isMovingDown
          }
        ],
        
        [OverheadReachState.UP]: [
          {
            targetState: OverheadReachState.LOWERING,
            condition: (metrics, stateData, timestamp) => 
              metrics.isMovingDown && !metrics.isAboveHead
          },
          {
            targetState: OverheadReachState.DOWN,
            condition: (metrics, stateData) => 
              metrics.isBelowShoulder
          }
        ],
        
        [OverheadReachState.LOWERING]: [
          {
            targetState: OverheadReachState.DOWN,
            condition: (metrics, stateData) => 
              metrics.isBelowShoulder,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastBelowShoulderTime = timestamp;
            }
          },
          {
            targetState: OverheadReachState.RAISING,
            condition: (metrics, stateData) => 
              metrics.isMovingUp && !metrics.isBelowShoulder
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    const nose = landmarks[0];
    const leftWrist = landmarks[15];
    const rightWrist = landmarks[16];
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftElbow = landmarks[13];
    const rightElbow = landmarks[14];
    
    if (!nose || !leftWrist || !rightWrist || !leftShoulder || !rightShoulder || !leftElbow || !rightElbow) {
      return {};
    }
    
    const activeWrist = leftWrist.y < rightWrist.y ? leftWrist : rightWrist;
    const activeShoulder = leftWrist.y < rightWrist.y ? leftShoulder : rightShoulder;
    const activeElbow = leftWrist.y < rightWrist.y ? leftElbow : rightElbow;
    const inactiveWrist = leftWrist.y < rightWrist.y ? rightWrist : leftWrist;
    
    // Get world landmarks for accurate 3D angle calculation
    const activeWrist3D = worldLandmarks[leftWrist.y < rightWrist.y ? 15 : 16];
    const activeShoulder3D = worldLandmarks[leftWrist.y < rightWrist.y ? 11 : 12];
    const activeElbow3D = worldLandmarks[leftWrist.y < rightWrist.y ? 13 : 14];
    
    const heightDifference = this.stateData.previousWristHeight - activeWrist.y;
    const isMovingUp = heightDifference < -MOVEMENT_THRESHOLD;
    const isMovingDown = heightDifference > MOVEMENT_THRESHOLD;
    
    let velocityScore = this.stateData.velocityScore || 0;
    const timeDelta = timestamp - (this.stateData.previousTimestamp || timestamp);
    
    if (timeDelta > 0) {
      const velocity = Math.abs(heightDifference) / (timeDelta / 1000);
      velocityScore = Math.min(100, Math.max(0, (velocity / 0.5) * 100));
    }
    
    const exerciseProgress = Math.min(100, Math.max(0, 100 - (activeWrist.y * 100)));
    
    const isAboveHead = activeWrist.y < (nose.y - 0.10) && 
      Math.abs(activeWrist.x - activeShoulder.x) < 0.25 &&  
      activeWrist.z < activeShoulder.z + 0.3;
    
    const isBelowShoulder = activeWrist.y > (activeShoulder.y + 0.15);
    
    // Calculate elbow angle to ensure arm is reasonably straight
    const elbowAngle = calculateJointAngle(activeShoulder3D, activeElbow3D, activeWrist3D);
    
    // Check if wrist is vertically aligned above shoulder (not off to the side)
    const lateralOffset = Math.abs(activeWrist.x - activeShoulder.x);
    
    // Calculate ROM based on height reached - GRADUAL, not binary
    let rangeOfMotion = 0;
    
    if (isBelowShoulder) {
      // Wrist is below shoulder - no range of motion
      rangeOfMotion = 0;
    } else {
      // Simplified ROM calculation: how high is wrist above shoulder
      // In MediaPipe, lower y = higher position on screen
      const wristAboveShoulderDistance = activeShoulder.y - activeWrist.y;
      
      // Use shoulder-to-nose as a reference for "good overhead reach"
      const shoulderToNoseDistance = Math.abs(activeShoulder.y - nose.y);
      
      // Calculate base ROM
      // 0% = at shoulder level
      // 50% = at nose level  
      // 100% = well above head (1.5x shoulder-to-nose distance)
      let baseROM = 0;
      if (shoulderToNoseDistance > 0.05) {
        // Good landmarks - use proportional calculation
        const targetDistance = shoulderToNoseDistance * 1.5; // Full reach = 1.5x shoulder-nose distance
        baseROM = Math.min(100, Math.max(0, (wristAboveShoulderDistance / targetDistance) * 100));
      } else {
        // Fallback for poor landmarks: fixed distance
        baseROM = Math.min(100, Math.max(0, (wristAboveShoulderDistance / 0.45) * 100));
      }
      
      // Apply form quality multipliers (reduce ROM for poor form, but don't make it 0)
      // Arm straightness factor: <140° = 0.4x, 140-160° = 0.6-0.9x, >160° = 1.0x
      let straightnessFactor = 1.0;
      if (elbowAngle < 130) {
        // Very bent arm - significant penalty
        straightnessFactor = 0.4;
      } else if (elbowAngle < 160) {
        // Slightly bent - gradual penalty  
        straightnessFactor = 0.6 + ((elbowAngle - 130) / 30) * 0.4; // 0.6 to 1.0
      }
      
      // Vertical alignment factor: 0-15cm = 1.0x, 15-25cm = 0.7x, >25cm = 0.5x
      let alignmentFactor = 1.0;
      if (lateralOffset > 0.25) {
        // Way off to the side
        alignmentFactor = 0.5;
      } else if (lateralOffset > 0.15) {
        // Somewhat off to the side
        alignmentFactor = 0.7 + (0.25 - lateralOffset) / 0.10 * 0.3; // 0.7 to 1.0
      }
      
      // Final ROM = base height × form quality
      rangeOfMotion = Math.round(baseROM * straightnessFactor * alignmentFactor);
    }
    
    // Store form quality flags for feedback
    const isArmStraight = elbowAngle > 150;
    const isVerticallyAligned = lateralOffset < 0.2;
    
    const lastPositions = [...(this.stateData.lastPositions || [])];
    if (lastPositions.length >= 5) {
      lastPositions.shift();
    }
    lastPositions.push({y: activeWrist.y, timestamp});
    
    let movementSmoothness = 100;
    if (lastPositions.length >= 3) {
      let jerkSum = 0;
      for (let i = 2; i < lastPositions.length; i++) {
        const p1 = lastPositions[i-2];
        const p2 = lastPositions[i-1];
        const p3 = lastPositions[i];
        
        const dt1 = (p2.timestamp - p1.timestamp) / 1000;
        const dt2 = (p3.timestamp - p2.timestamp) / 1000;
        
        if (dt1 > 0 && dt2 > 0) {
          const acc1 = (p2.y - p1.y) / (dt1 * dt1);
          const acc2 = (p3.y - p2.y) / (dt2 * dt2);
          const jerk = Math.abs(acc2 - acc1) / ((dt1 + dt2) / 2);
          jerkSum += jerk;
        }
      }
      
      const avgJerk = jerkSum / (lastPositions.length - 2);
      movementSmoothness = Math.max(0, 100 - avgJerk * 5000);
    }
    
    return {
      activeWrist,
      activeShoulder,
      inactiveWrist,
      isAboveHead,
      isBelowShoulder,
      
      isMovingUp,
      isMovingDown,
      previousWristHeight: activeWrist.y,
      previousTimestamp: timestamp,
      lastPositions,
      
      exerciseProgress,
      rangeOfMotion,
      elbowAngle,
      lateralOffset,
      isArmStraight,
      isVerticallyAligned,
      movementSmoothness,
      velocityScore
    };
  }
  
  updateFormScores(metrics, transitioned) {
    // Update component scores from current metrics
    this.formScores.velocity = metrics.velocityScore || 0;
    this.formScores.control = metrics.movementSmoothness || 0;
    this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion || 0);
    
    // Calculate arm straightness score (for form score display)
    let straightnessScore = 100;
    if (metrics.elbowAngle !== undefined && !metrics.isBelowShoulder) {
      if (metrics.elbowAngle < 140) {
        straightnessScore = 40;
      } else if (metrics.elbowAngle < 160) {
        // Gradual from 60 to 95
        straightnessScore = 60 + ((metrics.elbowAngle - 140) / 20) * 35;
      } else if (metrics.elbowAngle < 170) {
        // Gradual from 95 to 100
        straightnessScore = 95 + ((metrics.elbowAngle - 160) / 10) * 5;
      }
    }
    
    // Calculate alignment score
    let alignmentScore = 100;
    if (metrics.lateralOffset !== undefined && !metrics.isBelowShoulder) {
      if (metrics.lateralOffset > 0.30) {
        alignmentScore = 50;
      } else if (metrics.lateralOffset > 0.15) {
        alignmentScore = 75 + (0.30 - metrics.lateralOffset) / 0.15 * 25;
      }
    }
    
    // Overall form score weighs: straightness (40%), alignment (30%), ROM (20%), control (10%)
    // Form score should reflect QUALITY of movement, not quantity
    // ROM is tracked separately - here it's just one component of good form
    const currentFormScore = Math.round(
      (straightnessScore * 0.4) +
      (alignmentScore * 0.3) +
      (this.formScores.rangeOfMotion * 0.2) + 
      (this.formScores.control * 0.1)
    );
    
    // Update overall form score based on whether a rep was just completed
    if (this.stateData.repFormScore) {
      // If rep just completed, blend the rep score into overall
      this.formScores.overall = Math.round(this.formScores.overall * 0.7 + this.stateData.repFormScore * 0.3);
      this.stateData.repFormScore = 0;
    } else {
      // Otherwise, use current form score (important for real-time feedback)
      this.formScores.overall = currentFormScore;
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    // Priority 1: Check if arm is straight (most important for overhead reach)
    if (metrics.elbowAngle !== undefined && !metrics.isArmStraight && !metrics.isBelowShoulder) {
      return "Straighten your arm for overhead reach";
    }
    
    // Priority 2: Check if arm is vertically aligned
    if (metrics.isArmStraight && !metrics.isVerticallyAligned && !metrics.isBelowShoulder) {
      return "Keep arm directly above shoulder (not to the side)";
    }
    
    // Priority 3: Other form cues when form is good
    if (metrics.isAboveHead) {
      if (metrics.inactiveWrist && metrics.activeShoulder && 
          metrics.inactiveWrist.y < metrics.activeShoulder.y) {
        return "Keep inactive arm down";
      }
      
      if (metrics.nose && metrics.activeWrist && 
          metrics.activeWrist.y > (metrics.nose.y - 0.25)) {
        return "Reach higher for full range of motion";
      }
    }
    
    return null;
  }
  
  updateStateData(metrics, timestamp) {
    super.updateStateData(metrics, timestamp);
    
    // Track maximum ROM achieved during RAISING and UP (while arm is elevated, not during lowering)
    if (this.currentState === OverheadReachState.RAISING || this.currentState === OverheadReachState.UP) {
      this.stateData.maxRangeOfMotion = Math.max(
        this.stateData.maxRangeOfMotion || 0,
        metrics.rangeOfMotion || 0
      );
    }
    
    if (this.currentState === OverheadReachState.UP && 
        timestamp - this.stateData.lastAboveHeadTime > 5000) {
      this.currentState = OverheadReachState.DOWN;
    }
  }
} 